import {
  FlexColumn,
  isValidImage,
  isValidLatitude,
  isValidLongitude,
  isValidVideo,
  MaterialIcon,
  MaterialIconName,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StatefulPopover } from 'baseui/popover';
import { LabelXSmall } from 'baseui/typography';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { MEDIA_FILE_TYPE_OPTIONS } from '../../../../constants/media';
import { useCamera } from '../../../../hooks/useCamera';
import { useStyles } from '../../../../hooks/useStyles';
import { gptAlarmTypeIconState, gptAlarmTypeNameState, gptAlarmTypeState } from '../../../../storage/gptStates';
import { capturingTimelineMapEventState } from '../../../../storage/streaming';
import { StreamingTimelineEvent } from '../../../../types/streaming';
import SafeGptAlarmIcon from '../../../shared/SafeGptAlarmIcon';

type Props = Readonly<{
  event: StreamingTimelineEvent;
  offset: number;
}>;

function CapturingVideoTimelineEvent({ event, offset }: Props) {
  const { theme } = useStyles();
  const { formatDateFromTs } = useMoment();
  const { zoomTo } = useCamera();
  const setMapEvent = useSetRecoilState(capturingTimelineMapEventState);
  const alarmType = useRecoilValue(gptAlarmTypeNameState(event?.alarmType));
  const alarmData = useRecoilValue(gptAlarmTypeState(event?.alarmType));
  const alarmIcon = useRecoilValue(gptAlarmTypeIconState(event?.alarmType));

  const eventIcon: MaterialIconName = (() => {
    if (event.alarmType && event.alarmType > -1) {
      return alarmIcon ? '' : 'warning';
    }
    if (event.key) {
      if (isValidImage(event.key)) {
        return 'image';
      }
      if (isValidVideo(event.key)) {
        return 'videocam';
      }
    }
    return 'place';
  })();

  const onLocateEvent = () => {
    setMapEvent(event);
    if (isValidLatitude(event.latitude) && isValidLongitude(event.longitude)) {
      zoomTo({
        lat: event.latitude,
        lng: event.longitude,
      });
    }
  };

  return (
    <StatefulPopover
      content={() => (
        <FlexColumn
          padding={theme.sizing.scale300}
          gap={theme.sizing.scale0}
        >
          {alarmType && <LabelXSmall>{alarmType}</LabelXSmall>}
          <LabelXSmall color={theme.colors.contentTertiary}>{formatDateFromTs(+event.created)}</LabelXSmall>
          {event.fileType && (
            <LabelXSmall color={theme.colors.contentTertiary}>
              {MEDIA_FILE_TYPE_OPTIONS.find((option) => option.id === event.fileType)?.label ?? '-'}
            </LabelXSmall>
          )}
        </FlexColumn>
      )}
      placement="top"
      triggerType="hover"
      ignoreBoundary
      popoverMargin={5}
      overrides={{
        Inner: {
          style: {
            backgroundColor: theme.colors.backgroundPrimary,
          },
        },
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '55px',
          left: offset,
          transform: 'translateX(-50%)',
        }}
      >
        <Button
          size="mini"
          kind="primary"
          shape="circle"
          onClick={onLocateEvent}
          colors={
            alarmData && {
              backgroundColor: alarmData.color || theme.colors.buttonPrimaryFill,
              color: 'white',
            }
          }
          overrides={{
            BaseButton: {
              style: {
                width: theme.sizing.scale800,
                height: theme.sizing.scale800,
              },
            },
          }}
        >
          {!eventIcon && alarmIcon && (
            <SafeGptAlarmIcon
              url={alarmIcon}
              size={theme.sizing.scale600}
              color={theme.colors.contentInversePrimary}
            />
          )}
          {!alarmIcon && (
            <MaterialIcon
              name={eventIcon}
              size={theme.sizing.scale600}
              color={theme.colors.contentInversePrimary}
            />
          )}
        </Button>
      </span>
    </StatefulPopover>
  );
}

export default CapturingVideoTimelineEvent;
